// The questions a firm added to its Magic Link (SVSP-218). They never affect pricing, so they are
// optional for the client and are asked after the default questions of the same screen.

export type FirmAnswerType = "yes_no" | "multi_select";
export type FirmFlow = "business" | "personal";

export type FirmQuestion = {
  id: number;
  screen: string;
  flow: FirmFlow;
  text: string;
  answer_type: FirmAnswerType;
  options: string[];
};

export type FirmAnswers = Record<number, string[]>;

export type FirmAnswerAttributes = {
  question_bank_question_id: number;
  answer: string[];
};

export const YES = "Yes";
export const NO = "No";

// The survey calls the individual path "individual"; the question bank calls it "personal".
export const flowOf = (taxPayerType: string): FirmFlow =>
  taxPayerType === "individual" ? "personal" : "business";

export const questionsForScreen = (
  questions: FirmQuestion[],
  screen: string,
  taxPayerType: string
): FirmQuestion[] => {
  const flow = flowOf(taxPayerType);
  return questions.filter(
    (question) => question.screen === screen && question.flow === flow
  );
};

export const toggleChoice = (current: string[], option: string): string[] =>
  current.includes(option)
    ? current.filter((choice) => choice !== option)
    : [...current, option];

// Every question the client was shown is sent, answered or not: an empty answer is what tells the
// tax pro the question was asked and skipped, which is different from never having been asked.
export const toAnswerAttributes = (
  answers: FirmAnswers,
  questions: FirmQuestion[],
  taxPayerType: string
): FirmAnswerAttributes[] => {
  const flow = flowOf(taxPayerType);
  return (questions || [])
    .filter((question) => question.flow === flow)
    .map((question) => ({
      question_bank_question_id: question.id,
      answer: answers[question.id] || [],
    }));
};

export const parseFirmQuestions = (payload: unknown): FirmQuestion[] => {
  const questions = (payload as { data?: { questions?: FirmQuestion[] } })?.data
    ?.questions;
  return Array.isArray(questions) ? questions : [];
};

// Sub-step 2 of "About You" asks a different question per persona: 1.1/1.4 is "What's Missing",
// 1.2/1.5 is "Why Interested" and 1.3/1.6 belongs to a client the firm already has.
export const screenForAboutUsStep = (step: number | string): string | null => {
  const value = Number(step);
  if (value === 1.1 || value === 1.4) {
    return "whats_missing";
  }
  if (value === 1.2 || value === 1.5) {
    return "why_interested";
  }
  return null;
};
